// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ChatPage.test.tsx
import {
  createContext,
  type MutableRefObject,
  type ReactNode,
  useContext,
  useRef,
} from "react";

import { useAttachedFiles } from "../../hooks/useAttachedFiles";

export type AskV2AttachmentDraft = ReturnType<typeof useAttachedFiles>;

const AskV2AttachmentDraftContext = createContext<AskV2AttachmentDraft | null>(
  null,
);

type AskV2AttachmentDraftRoot = {
  draft: AskV2AttachmentDraft;
  pendingCreatedChatIdRef: MutableRefObject<string | null>;
  routeScopeRef: MutableRefObject<string | null>;
};

const AskV2AttachmentDraftRootContext =
  createContext<AskV2AttachmentDraftRoot | null>(null);

export function AskV2AttachmentDraftRootProvider({
  children,
}: {
  children: ReactNode;
}) {
  const draft = useAttachedFiles();
  const pendingCreatedChatIdRef = useRef<string | null>(null);
  const routeScopeRef = useRef<string | null>(null);

  return (
    <AskV2AttachmentDraftRootContext.Provider
      value={{ draft, pendingCreatedChatIdRef, routeScopeRef }}
    >
      {children}
    </AskV2AttachmentDraftRootContext.Provider>
  );
}

function LocalAskV2AttachmentDraftProvider({
  children,
}: {
  children: ReactNode;
}) {
  const draft = useAttachedFiles();
  return (
    <AskV2AttachmentDraftContext.Provider value={draft}>
      {children}
    </AskV2AttachmentDraftContext.Provider>
  );
}

export function AskV2AttachmentDraftProvider({
  children,
}: {
  children: ReactNode;
}) {
  const root = useContext(AskV2AttachmentDraftRootContext);

  if (!root) {
    return (
      <LocalAskV2AttachmentDraftProvider>
        {children}
      </LocalAskV2AttachmentDraftProvider>
    );
  }

  return (
    <AskV2AttachmentDraftContext.Provider value={root.draft}>
      {children}
    </AskV2AttachmentDraftContext.Provider>
  );
}

export function useAskV2AttachmentDraft(): AskV2AttachmentDraft | null {
  return useContext(AskV2AttachmentDraftContext);
}

export function useAskV2AttachmentDraftRoot(): AskV2AttachmentDraftRoot | null {
  return useContext(AskV2AttachmentDraftRootContext);
}
